// ============================================================
// CreateClientDialog.js
// Modal form for creating a Client. Tenant dropdown is populated
// from listTenants() on open; "No tenant (Pathway 3)" is the
// null option. clientName + clientType + optional tenantId.
// LifecycleStatus is NOT in this form — backend defaults to
// 'trial' (Workstream 1 design decision).
// ============================================================

import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { createAdminClient } from '../api/adminClients';
import { listTenants } from '../api/tenants';
import { translateError } from '../utils/errorMessages';
import { toast } from '../components/Toaster';
import './CreateTenantDialog.css';

const INITIAL_STATE = {
  clientName: '',
  clientType: 'care_home',
  tenantId:   '',  // empty string === Pathway 3 / null
};

const CLIENT_TYPE_LABELS = {
  care_home:          'Care home',
  domiciliary_agency: 'Domiciliary agency',
  mixed:              'Mixed',
};

export default function CreateClientDialog({ isOpen, onClose, onCreated, defaultTenantId }) {
  const [form, setForm] = useState(INITIAL_STATE);
  const [tenants, setTenants] = useState([]);
  const [tenantsError, setTenantsError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Reset form on open; pre-fill tenantId if provided via query param.
  useEffect(() => {
    if (!isOpen) return;
    setForm({
      ...INITIAL_STATE,
      tenantId: defaultTenantId != null ? String(defaultTenantId) : '',
    });
    setErrors({});
    setSubmitError(null);
  }, [isOpen, defaultTenantId]);

  // Load tenants when the dialog opens. Fail-soft — the operator
  // can still create a Pathway-3 client (no tenant) if this fails.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setTenantsError(null);
    listTenants({ pageSize: 200 }).then((res) => {
      if (!cancelled) setTenants(res.items || []);
    }).catch((err) => {
      if (!cancelled) setTenantsError(translateError(err));
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  function handleClose() {
    if (isSubmitting) return;
    onClose();
  }

  function validate() {
    const e = {};
    if (!form.clientName.trim()) e.clientName = 'Client name is required.';
    if (form.clientName.length > 200) e.clientName = 'Client name must be 200 characters or fewer.';
    if (!form.clientType) e.clientType = 'Client type is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        clientName: form.clientName.trim(),
        clientType: form.clientType,
      };
      if (form.tenantId) body.tenantId = parseInt(form.tenantId, 10);
      const created = await createAdminClient(body);
      toast.success(`Client "${created.clientName}" created (trial)`);
      onCreated(created);
      onClose();
    } catch (err) {
      if (err && err.field) {
        setErrors({ [err.field]: translateError(err) });
      } else {
        setSubmitError(translateError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Client" closeDisabled={isSubmitting}>
      <form className="create-tenant-form" onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="create-tenant-form__banner" role="alert">{submitError}</div>
        )}
        {tenantsError && (
          <div className="create-tenant-form__banner" role="alert">
            Could not load tenants — you can still create a Pathway-3 client.
          </div>
        )}

        <Field label="Client name" error={errors.clientName} required>
          <input
            type="text"
            value={form.clientName}
            maxLength={200}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, clientName: e.target.value })}
            autoFocus
          />
        </Field>

        <Field label="Client type" error={errors.clientType} required>
          <select
            value={form.clientType}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, clientType: e.target.value })}
          >
            {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Tenant"
          error={errors.tenantId}
          help="Leave as 'No tenant' for a Pathway-3 client (no MSP tenant management)."
        >
          <select
            value={form.tenantId}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
          >
            <option value="">No tenant (Pathway 3)</option>
            {tenants.map((t) => (
              <option key={t.tenantId} value={String(t.tenantId)}>{t.tenantName}</option>
            ))}
          </select>
        </Field>

        <div className="create-tenant-form__actions">
          <button
            type="button"
            className="create-tenant-form__cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="create-tenant-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={14} className="create-tenant-form__spinner" />}
            Create client
          </button>
        </div>
      </form>
    </Modal>
  );
}
